import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TwitterOutlined, InstagramOutlined, LinkedinOutlined } from '@ant-design/icons';
import { useTranslation } from '../i18n/LanguageContext';

const languages = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const Footer = () => {
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { t, language, changeLanguage } = useTranslation();

  const footerLinks = [
    { label: t('nav.about'), path: '/about' },
    { label: t('nav.privacy'), path: '/privacy' },
    { label: t('nav.terms'), path: '/terms' },
    { label: t('nav.deleteAccount'), path: '/delete-account' },
    { label: t('nav.contact'), path: '/contact' },
  ];

  const socialLinks = [
    { icon: <TwitterOutlined style={{ fontSize: '20px' }} />, href: 'https://twitter.com', label: 'Twitter' },
    { icon: <InstagramOutlined style={{ fontSize: '20px' }} />, href: 'https://instagram.com', label: 'Instagram' },
    { icon: <LinkedinOutlined style={{ fontSize: '20px' }} />, href: 'https://linkedin.com', label: 'LinkedIn' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <footer
      style={{
        backgroundColor: '#3A3A3A',
      }}
      className="text-white py-16 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12"
        >
          {/* Brand Section */}
          <motion.div variants={itemVariants}>
            <img
              src="/logo/derkenar-logo.svg"
              alt="Derkenar Logo"
              style={{
                height: '40px',
                width: 'auto',
                marginBottom: '12px',
              }}
            />
            <p className="font-filson font-medium text-sm text-gray-300 leading-relaxed">
              {t('footer.brandDescription')}
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h4 className="font-filson font-bold text-lg mb-4" style={{ color: '#d5b76d' }}>
              {t('footer.quickLinks')}
            </h4>
            <ul className="flex flex-col gap-2">
              {footerLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="font-filson font-medium text-sm transition-colors"
                    style={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#d5b76d';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social Links */}
          <motion.div variants={itemVariants}>
            <h4 className="font-filson font-bold text-lg mb-4" style={{ color: '#d5b76d' }}>
              {t('footer.followUs')}
            </h4>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(213, 183, 109, 0.1)',
                    borderRadius: '8px',
                    fontSize: '20px',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d5b76d';
                    e.currentTarget.style.color = '#1a1a1a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(213, 183, 109, 0.1)';
                    e.currentTarget.style.color = 'white';
                  }}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <div
          style={{
            height: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            marginBottom: '24px',
          }}
        />

        {/* Bottom */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-6"
        >
          <div>
            <p className="font-filson font-medium text-sm text-gray-400 mb-2">
              {t('footer.copyright')}
            </p>
            <p className="font-filson font-medium text-sm text-gray-400">
              {t('footer.madeBy')}
            </p>
          </div>

          {/* Language Selector Footer */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #999',
                backgroundColor: 'transparent',
                color: '#999',
                fontFamily: "'Filson Soft', sans-serif",
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.3s',
              }}
            >
              {languages.find(l => l.code === language)?.label}
              <span style={{ fontSize: '10px' }}>▼</span>
            </button>
            {isLanguageOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: '4px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #555',
                  borderRadius: '6px',
                  boxShadow: '0 -4px 12px rgba(0,0,0,0.3)',
                  zIndex: 100,
                  minWidth: '120px',
                }}
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsLanguageOpen(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 12px',
                      textAlign: 'left',
                      backgroundColor: language === lang.code ? '#444' : 'transparent',
                      color: language === lang.code ? '#d5b76d' : '#999',
                      border: 'none',
                      fontFamily: "'Filson Soft', sans-serif",
                      fontSize: '13px',
                      fontWeight: language === lang.code ? '600' : '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#444';
                      e.target.style.color = '#d5b76d';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = language === lang.code ? '#444' : 'transparent';
                      e.target.style.color = language === lang.code ? '#d5b76d' : '#999';
                    }}
                  >
                    {lang.label}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
